import React, { useCallback, useEffect, useState } from 'react';
import repairOrdersService from '../../../services/repairOrders.service';
import { RepairOrderPing } from '../../../types/repairOrders';
import { formatPingAge } from '../../../components/RepairOrderCardList';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { useToast } from '../../../contexts/ToastContext';

/** Row label per ping type — mirrors the chip labels on RepairOrderCardList. */
const PING_LABELS: Record<string, string> = {
  eta: 'ETA requested',
  completion: 'Completion requested',
};

export interface OpenPingsSectionProps {
  /** The repair order being viewed. null = create mode: nothing to ping about. */
  orderRecno: number | null;
  /** The drawer's open state — the fetch is gated on it so a closed drawer is silent. */
  isOpen: boolean;
}

/**
 * The open maintenance requests on one repair order, with a Dismiss control.
 *
 * A ping auto-closes when it is ANSWERED (setting a vendor ETA closes an `eta`
 * ping; completing the order closes a `completion` ping, both in the answer's
 * own transaction). This section covers the other outcome: maintenance is not
 * going to answer — the order was cancelled, the question is moot, it was asked
 * in error — and without a dismiss the ping sits open forever.
 *
 * Renders NOTHING when there are no open pings: no heading, no empty state. An
 * always-present "Open Requests (0)" box would be noise on the overwhelming
 * majority of repair orders.
 *
 * The ROWS are ungated on purpose — an outstanding request is information, and
 * maintenance staff without the dismiss grant still need to see that somebody
 * is waiting on them. Only the Dismiss BUTTON sits behind
 * `repair-order-ping-clear`.
 */
const OpenPingsSection: React.FC<OpenPingsSectionProps> = ({ orderRecno, isOpen }) => {
  const [pings, setPings] = useState<RepairOrderPing[]>([]);
  const [busyRecno, setBusyRecno] = useState<number | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!isOpen || orderRecno === null) {
      setPings([]);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    repairOrdersService
      .getPings([orderRecno], controller.signal)
      .then((res) => {
        if (cancelled) return;
        setPings(res.data?.[String(orderRecno)] || []);
      })
      .catch(() => {
        // Non-fatal: the drawer's real work is editing the order. A failed ping
        // fetch degrades to "no open requests" rather than blocking the form.
        if (!cancelled) setPings([]);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isOpen, orderRecno]);

  /**
   * Dismiss one ping.
   *
   * Deliberately NOT optimistic, unlike the request button on the ROs tab.
   * Requesting is cheap, idempotent and reversible; dismissing is irreversible
   * (the backend stamps cleared_at and writes a note), so showing the row as
   * gone before the server agrees can only ever mislead. Awaiting also means
   * there is no "restore the row" path to get wrong — the row simply never
   * leaves until the POST succeeds.
   */
  const handleDismiss = useCallback(async (ping: RepairOrderPing) => {
    if (orderRecno === null) return;
    setBusyRecno(ping.recno);
    try {
      await repairOrdersService.clearPing(orderRecno, ping.recno);
      setPings((prev) => prev.filter((p) => p.recno !== ping.recno));
      showToast({ type: 'success', message: 'Maintenance request dismissed.' });
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
      if (e?.response?.status === 404) {
        // The endpoint 404s when the ping is gone or already cleared — which
        // happens legitimately when someone ANSWERED it (auto-clear) between
        // this drawer loading and the click. That is a good outcome, not an
        // error: drop the row and say so plainly.
        setPings((prev) => prev.filter((p) => p.recno !== ping.recno));
        showToast({ type: 'info', message: 'That request was already resolved.' });
      } else {
        const detail = e?.response?.data?.detail;
        showToast({
          type: 'error',
          message: `Dismiss failed: ${typeof detail === 'string' ? detail : e?.message || 'Request failed'}`,
        });
      }
    } finally {
      setBusyRecno(null);
    }
  }, [orderRecno, showToast]);

  if (orderRecno === null || pings.length === 0) return null;

  return (
    <div
      data-testid="ro-open-pings"
      className="mx-4 sm:mx-6 mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20"
    >
      <h3 className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">
        Open Maintenance Requests
      </h3>
      <div className="space-y-1">
        {pings.map((p, index) => (
          <div
            key={p.recno}
            data-testid="ro-open-ping-row"
            className="flex items-center gap-2 text-xs text-amber-900 dark:text-amber-100"
          >
            <span className="min-w-0 truncate">
              {[
                PING_LABELS[p.requestType] || 'Requested',
                formatPingAge(p.requestedAt),
                p.requestedBy,
              ].filter(Boolean).join(' · ')}
            </span>
            <PermissionsContainer
              componentId="repair-order-ping-clear"
              componentName="Dismiss Maintenance Ping"
              defaultHidden
              // Repeated instances share one componentId; decorate only the
              // first row in permissions mode.
              showHandle={index === 0}
            >
              <button
                type="button"
                onClick={() => { void handleDismiss(p); }}
                disabled={busyRecno === p.recno}
                aria-label={`Dismiss ${PING_LABELS[p.requestType] || 'request'} from ${p.requestedBy}`}
                className="flex-shrink-0 rounded-full border border-amber-400 px-2 py-0.5 text-[0.625rem] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/40 transition-colors"
              >
                {busyRecno === p.recno ? 'Dismissing…' : 'Dismiss'}
              </button>
            </PermissionsContainer>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OpenPingsSection;
